import {
  api_fetch,
  DeductionDetailJson,
  DeductionExt,
  generate_message,
  Message,
  reply_to_thread,
} from "@/api"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import { triggerRefreshFlag } from "@/refresh_flag"
import { format_transaction_amount, useAsyncEffect } from "@/utils"
import { format } from "date-fns"
import { ArrowUp, Loader2 } from "lucide-react"
import { useState } from "preact/hooks"
import { entity, persistence } from "simpler-state"

// determines if dialog is open or closed
export const BumpMessageDeduction = entity<DeductionExt | null>(null, [
  persistence("bump-message-deduction", { storage: "session" }),
])

interface MessageItemProps {
  message: Message
}

function MessageItem({ message }: MessageItemProps) {
  const formattedDate = format(new Date(message.date), "MMM d, yyyy")
  const firstLetter = message.from.email?.[0] ?? message.from.name?.[0] ?? "?"

  return (
    <div class="bg-white p-3 rounded-md border-gray-100 border flex w-full">
      <div class="w-10 mt-1">
        <div class="flex items-center justify-center bg-orange-500 text-white rounded-full w-8 h-8 text-sm font-medium">
          {firstLetter.toUpperCase()}
        </div>
      </div>
      <div class="w-full">
        <div class="text-gray-500 text-sm">
          <div class="flex items-center justify-between">
            <span>
              From: {message.from.name || message.from.email}
            </span>
            <span class="text-nowrap">{formattedDate}</span>
          </div>
          <div class="flex justify-between">
            <span class="truncate">{message.subject}</span>
            <span>{message.source === "freshdesk" ? "Freshdesk" : "Email"}</span>
          </div>
        </div>
        <div class="mt-2 text-sm line-clamp-3" dangerouslySetInnerHTML={{ __html: message.html_body }} />
      </div>
    </div>
  )
}

export function BumpMessageDialog() {
  const deduction = BumpMessageDeduction.use(d => d)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState("")
  const [fullDeduction, setFullDeduction] = useState<DeductionDetailJson | null>(null)

  useAsyncEffect(async () => {
    if (!deduction) {
      setFullDeduction(null)
      setMessage("")
      return
    }

    setLoading(true)

    // Fetch full deduction details including messages
    const detailRes = await api_fetch<DeductionDetailJson>(`/deduction/${deduction.id}`)
    if (detailRes.ok) {
      setFullDeduction(detailRes.value.data)
    }

    // Generate bump message
    const bumpRes = await generate_message(deduction.id, "bump_message")
    if (bumpRes.ok) {
      setMessage(bumpRes.value.data.message)
    }

    setLoading(false)
  }, [deduction?.id])

  async function handleSendMessage() {
    if (!message || !fullDeduction) return

    const messages = fullDeduction.messages
    if (!messages.length) {
      toast({
        title: "No thread found",
        description: "Cannot send a bump message without an existing conversation thread",
        variant: "destructive",
      })
      return
    }

    const lastMessage = messages[messages.length - 1]
    setSending(true)

    const res = await reply_to_thread(fullDeduction.id, lastMessage.thread_id, message)
    setSending(false)

    if (res.ok) {
      toast({
        title: "Message sent",
        description: "Your follow-up message has been sent",
      })
      BumpMessageDeduction.set(null)
      triggerRefreshFlag()
    } else {
      toast({
        title: "Error",
        description: res.error.message,
        variant: "destructive",
      })
    }
  }

  if (!deduction) {
    return null
  }

  const messages = fullDeduction?.messages ?? []

  return (
    <Dialog open={true} onOpenChange={open => (open ? null : BumpMessageDeduction.set(null))}>
      <DialogContent class="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bump Message</DialogTitle>
          <DialogDescription>
            Send a follow-up message to continue the conversation
          </DialogDescription>
        </DialogHeader>

        {/* Deduction Details */}
        <div class="border rounded-md p-3 bg-gray-50">
          <div class="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span class="text-gray-500">Invoice #:</span>{" "}
              <span class="font-medium">{deduction.invoice_number}</span>
            </div>
            <div>
              <span class="text-gray-500">Amount:</span>{" "}
              <span class="font-medium">{format_transaction_amount(deduction.kind, deduction.invoice_amount)}</span>
            </div>
            <div>
              <span class="text-gray-500">Source:</span>{" "}
              <span class="font-medium capitalize">{deduction.source}</span>
            </div>
            <div>
              <span class="text-gray-500">Category:</span>{" "}
              <span class="font-medium">{deduction.category}</span>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div class="space-y-2">
          <h4 class="text-sm font-medium text-gray-700">Conversation</h4>
          {loading ? (
            <div class="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : messages.length === 0 ? (
            <div class="text-sm text-gray-500 italic py-4 text-center">
              No messages found
            </div>
          ) : (
            <div class="space-y-2 max-h-64 overflow-y-auto">
              {messages.map(msg => (
                <MessageItem key={msg.id} message={msg} />
              ))}
            </div>
          )}
        </div>

        {/* Reply Box */}
        <div class="space-y-2">
          <h4 class="text-sm font-medium text-gray-700">Your Message</h4>
          <Textarea
            value={message}
            onChange={(e: any) => setMessage(e.target.value)}
            onKeyDown={async (e: any) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault()
                await handleSendMessage()
              }
            }}
            class="min-h-[120px] resize-none"
            placeholder={loading ? "Generating message..." : "Enter your follow-up message..."}
            disabled={loading}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => BumpMessageDeduction.set(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            disabled={!message || sending || loading || messages.length === 0}>
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send <ArrowUp className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
